import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import { QueryState } from "@/components/common/AsyncState";
import { createSubject, fetchSubjects, updateSubject } from "@/api/branches";
import { formatMoney } from "@/utils/format";
import type { Subject } from "@/types/branch";

function SubjectDialog({ open, onClose, subject }: { open: boolean; onClose: () => void; subject?: Subject }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(subject?.name ?? "");
  const [levels, setLevels] = useState((subject?.levels ?? []).join(", "));
  const [packPrice, setPackPrice] = useState(subject?.packPrice ?? 0);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        levels: levels
          .split(",")
          .map((l) => l.trim())
          .filter(Boolean),
        packPrice,
      };
      return subject ? updateSubject(subject.id, payload) : createSubject(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subjects"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{subject ? "Редактирование предмета" : "Новый предмет"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Название предмета" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="Уровни (через запятую)"
            value={levels}
            onChange={(e) => setLevels(e.target.value)}
            helperText="Например: Beginner, Elementary, Intermediate"
          />
          <TextField label="Цена пакета (12 уроков)" type="number" value={packPrice} onChange={(e) => setPackPrice(Number(e.target.value))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!name || mutation.isPending} onClick={() => mutation.mutate()}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SubjectsTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });
  const [dialogState, setDialogState] = useState<{ open: boolean; subject?: Subject }>({ open: false });

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogState({ open: true })}>
          Новый предмет
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Предмет</TableCell>
              <TableCell>Уровни</TableCell>
              <TableCell>Цена пакета</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((subject) => (
              <TableRow key={subject.id}>
                <TableCell>{subject.name}</TableCell>
                <TableCell>{subject.levels.join(", ")}</TableCell>
                <TableCell>{formatMoney(subject.packPrice)}</TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setDialogState({ open: true, subject })}>
                    Изменить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueryState>
      <SubjectDialog open={dialogState.open} subject={dialogState.subject} onClose={() => setDialogState({ open: false })} />
    </>
  );
}
